import { useNow } from '../../lib/useNow'
import { SCHEDULE, formatCountdown, formatRange, scheduleStatus, type ScheduleStatus } from '../../event/schedule'

function StatusLine({ status }: { status: ScheduleStatus }) {
  if (status.phase === 'before') {
    return (
      <>
        <p className="text-sm font-bold text-[var(--page-accent-text)]">Starts in</p>
        <p className="mt-1 text-3xl font-black tabular-nums">{formatCountdown(status.msUntilNext)}</p>
        <p className="mt-1 text-[var(--page-ink-muted)]">First up: {status.next.title}</p>
      </>
    )
  }
  if (status.phase === 'active') {
    return (
      <>
        <p className="text-sm font-bold text-[var(--page-accent-text)]">Happening now</p>
        <p className="mt-1 text-2xl font-black">{status.current.title}</p>
        <p className="mt-1 text-[var(--page-ink-muted)]">
          <span className="tabular-nums">{formatCountdown(status.msUntilEnd)}</span> left
          {status.next ? `, then ${status.next.title}` : ''}
        </p>
      </>
    )
  }
  return (
    <>
      <p className="text-sm font-bold text-[var(--page-accent-text)]">That's a wrap</p>
      <p className="mt-1 text-2xl font-black">Thanks for building with us.</p>
    </>
  )
}

/**
 * The live schedule. Everything here is derived from `SCHEDULE`, so the
 * highlighted block and the countdown cannot disagree with the listed times.
 *
 * The countdown ticks every second, so it is not a live region: announcing it
 * would talk over the reader. The block list carries `aria-current` instead.
 */
export default function ScheduleSection({ headingLevel = 2 }: { headingLevel?: 2 | 3 }) {
  const now = useNow()
  const status = scheduleStatus(now)
  const currentId = status.phase === 'active' ? status.current.id : null
  const Heading = `h${headingLevel}` as const

  return (
    <section id="schedule" aria-labelledby="schedule-heading" className="scroll-mt-4">
      <Heading id="schedule-heading" className="text-2xl font-black tracking-tight">
        Schedule
      </Heading>
      <p className="mt-1 text-sm text-[var(--page-ink-muted)]">All times Eastern.</p>

      <div className="mt-4 rounded-lg border border-[var(--page-rule)] bg-[var(--page-canvas-sunken)] p-5">
        <StatusLine status={status} />
      </div>

      <ol className="mt-4 divide-y divide-[var(--page-rule)] rounded-lg border border-[var(--page-rule)]">
        {SCHEDULE.map((block) => {
          const isCurrent = block.id === currentId
          return (
            <li
              key={block.id}
              aria-current={isCurrent ? 'step' : undefined}
              className={`flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-6 ${
                isCurrent ? 'border-l-4 border-l-[var(--color-accent-500)] bg-[var(--page-canvas-sunken)]' : ''
              }`}
            >
              <span className="shrink-0 font-semibold tabular-nums sm:w-44">{formatRange(block)}</span>
              <span>
                <span className="font-semibold">{block.title}</span>
                {block.detail && <span className="block text-sm text-[var(--page-ink-muted)]">{block.detail}</span>}
                {isCurrent && <span className="sr-only"> (happening now)</span>}
              </span>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
